import { useContext, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { haveValue, toast, getUserDisplayName } from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import UserIcon from "../../assets/img/user-icon.svg";
import {
  updateTenantShareRequest,
  getTenantShareRequests,
} from "../../services/sharedService";
import LandLordContext from "../../context/landlord/LandLordContext";

const getInitials = (name = "") => {
  const parts = String(name || "")
    .trim()
    .split(" ")
    .filter(Boolean);

  if (!parts.length) return "--";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const formatIncome = (value, isAnnual = false) => {
  if (!haveValue(value) || Number.isNaN(Number(value))) return "-";
  const monthlyValue = isAnnual ? Number(value) / 12 : Number(value);
  if (monthlyValue === 0) {
    return null;
  }
  return `${monthlyValue.toLocaleString("es-ES")}€/mo`;
};

const normalizeApplications = (raw = [], _t) => {
  if (!Array.isArray(raw)) return [];

  return raw.map((request, index) => {
    const tenant = request?.tenant_id;
    const application = request?.application_id;
    const isDirectShare = !(application?.participants?.length > 0);

    const tenantFullName =
      `${tenant?.fname || ""} ${tenant?.lname || ""}`.trim();
    const tenantInitials = getInitials(tenantFullName);
    const hasGuarantor =
      tenant?.have_gurantor === "true" || tenant?.have_gurantor === true;

    const candidates = [];

    if (isDirectShare) {
      candidates.push({
        id: tenant?._id || `tenant-${index}`,
        fullName: tenantFullName || "Inquilino",
        initials: tenantInitials,
        income: tenant?.current_annual_income,
        isAnnualIncome: true,
        occupation:
          _t(tenant?.employment_status) || _t(tenant?.employmentStatus) || null,
        hasGuarantor,
        email: tenant?.email,
      });
    } else {
      const participants = application?.participants || [];
      participants.forEach((participant, participantIndex) => {
        const tenantInfo = participant?.tenant_info;
        const participantName = tenantInfo
          ? getUserDisplayName(tenantInfo)
          : participant?.name || `Participant ${participantIndex + 1}`;
        const isMainTenant = participant?.id === tenant?._id;

        candidates.push({
          id: participant?.id || `participant-${index}-${participantIndex}`,
          fullName: participantName,
          initials: getInitials(participantName),
          income: tenantInfo
            ? tenantInfo?.current_annual_income
            : isMainTenant
              ? tenant?.current_annual_income
              : 0,
          isAnnualIncome: false,
          occupation: tenantInfo
            ? _t(tenantInfo?.employment_status) ||
              _t(tenantInfo?.employmentStatus) ||
              null
            : isMainTenant
              ? tenant?.employment_status || tenant?.employmentStatus || null
              : null,
          hasGuarantor: tenantInfo
            ? tenantInfo?.have_gurantor === "true" ||
              tenantInfo?.have_gurantor === true
            : isMainTenant && hasGuarantor,
          email: participant?.email,
          consentStatus:
            participant?.consent_status || participant?.consentStatus || "-",
        });
      });
    }

    return {
      id: request?._id || request?.id || index,
      checked: false,
      title: application?.title || `Nueva candidatura: ${tenantFullName}`,
      isDirectShare,
      candidates,
      applicationId: application?._id,
    };
  });
};

const PendingApplicationsModal = ({
  isOpen,
  onClose,
  loading,
  applications,
  workspaces,
  onRefresh,
}) => {
  const { _t } = useTranslator();
  const [selectedWorkspace, setSelectedWorkspace] = useState("");
  const [checkedApplications, setCheckedApplications] = useState(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedApplications, setExpandedApplications] = useState(new Set());
  const { fetchAllLandlordNotification } = useContext(LandLordContext);

  const normalizedApplications = useMemo(
    () => normalizeApplications(applications, _t),
    [applications, _t]
  );

  const toggleExpand = (appId) => {
    setExpandedApplications((prev) => {
      const next = new Set(prev);
      if (next.has(appId)) {
        next.delete(appId);
      } else {
        next.add(appId);
      }
      return next;
    });
  };

  const handleCheckboxChange = (appId) => {
    setCheckedApplications((prev) => {
      const next = new Set(prev);
      if (next.has(appId)) {
        next.delete(appId);
      } else {
        next.add(appId);
      }
      return next;
    });
  };

  const handleUpdateRequests = async (status) => {
    if (!selectedWorkspace || checkedApplications.size === 0) return;

    setIsProcessing(true);

    try {
      const promises = Array.from(checkedApplications).map((requestId) =>
        updateTenantShareRequest(requestId, {
          status,
          workspaceId: selectedWorkspace,
        })
      );

      const responses = await Promise.allSettled(promises);

      const allSuccessful = responses.every(
        (response) => response.status === "fulfilled"
      );

      if (allSuccessful) {
        await getTenantShareRequests();
        setCheckedApplications(new Set());

        const successMessage =
          status === "accepted"
            ? _t("shareRequestsAccepted") ||
              "Solicitudes aceptadas correctamente"
            : _t("shareRequestsRejected") ||
              "Solicitudes rechazadas correctamente";

        toast.success(successMessage);

        await fetchAllLandlordNotification();
        if (onRefresh) {
          onRefresh();
        }
        onClose();
      } else {
        const errorMessage =
          _t("someRequestsFailed") ||
          "Algunas solicitudes fallaron. Por favor, inténtalo de nuevo.";
        toast.error(errorMessage);
        console.error("Some requests failed");
      }
    } catch (error) {
      const errorMessage =
        _t("requestsUpdateError") ||
        "Error al actualizar las solicitudes. Por favor, inténtalo de nuevo.";
      toast.error(errorMessage);
      console.error("Error updating requests:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      className="newmodalui wrapper-modal pending-applications-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {_t("pendingApplications") || "Solicitudes pendientes"} (
          {normalizedApplications?.length || 0})
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="pending-applications-empty">
            {_t("loadingApplications") || "Cargando solicitudes..."}
          </div>
        ) : normalizedApplications.length === 0 ? (
          <div className="pending-applications-empty">
            {_t("noPendingApplications") || "No hay solicitudes pendientes."}
          </div>
        ) : (
          <div className="pending-applications-list">
            {normalizedApplications.map((application) => (
              <div key={application.id} className="pending-application-card">
                <div
                  className="pending-application-header-row"
                  onClick={() => toggleExpand(application.id)}
                  style={{ cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={checkedApplications.has(application.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleCheckboxChange(application.id)}
                  />
                  <div className="pending-application-title-icon">
                    {application.candidates.length === 1 ? (
                      <img src={UserIcon} alt="User" />
                    ) : (
                      <div className="position-relative">
                        <div className="mobile-profile-info-box">
                          <div className="userImageWrapper">
                            <img
                              src="/images/multiple_users_icon_unfilled.svg"
                              alt="User Icon"
                            />
                          </div>
                        </div>

                        {application.candidates.length > 0 && (
                          <div className="member-count-wrapper d-flex align-items-center justify-content-center">
                            {application.candidates.length}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="pending-application-title">
                    {application.title}
                  </p>
                  <span
                    style={{
                      marginLeft: "auto",
                      display: "flex",
                      alignItems: "center",
                      transition: "transform 0.3s ease",
                      transform: expandedApplications.has(application.id)
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </span>
                </div>

                <div
                  className="pending-application-collapsible"
                  style={{
                    maxHeight: expandedApplications.has(application.id)
                      ? `${application.candidates.length * 80}px`
                      : "0",
                    opacity: expandedApplications.has(application.id) ? 1 : 0,
                    overflow: "hidden",
                    transition: "max-height 0.3s ease, opacity 0.3s ease",
                  }}
                >
                  <div className="pending-application-candidates">
                    {application.candidates.map((candidate) => (
                      <div
                        key={candidate.id}
                        className="pending-application-candidate-row"
                      >
                        <div className="pending-application-avatar">
                          {candidate.initials}
                        </div>
                        <p className="pending-application-candidate-name">
                          {candidate.fullName}
                        </p>
                        <p className="pending-application-candidate-income">
                          {formatIncome(
                            candidate.income,
                            candidate.isAnnualIncome
                          )}
                        </p>
                        {candidate?.occupation && (
                          <p className="pending-application-candidate-occupation">
                            {candidate.occupation}
                          </p>
                        )}
                        {candidate.hasGuarantor && (
                          <span className="pending-application-guarantor">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="tabler-icon tabler-icon-shield-check"
                            >
                              <path d="M11.46 20.846a12 12 0 0 1 -7.96 -14.846a12 12 0 0 0 8.5 -3a12 12 0 0 0 8.5 3a12 12 0 0 1 -.09 7.06" />
                              <path d="M15 19l2 2l4 -4" />
                            </svg>
                            {_t("with_guarantor") || "with guarantor"}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="pending-application-footer-box">
          <p>
            {_t("selectWorkspaceToSaveRequest") ||
              "¿En qué workspace quieres guardar esta solicitud?"}
          </p>
          <select
            value={selectedWorkspace}
            onChange={(e) => setSelectedWorkspace(e.target.value)}
          >
            <option value="">
              {_t("selectWorkspace") || "Selecciona un workspace..."}
            </option>
            {(workspaces || []).map((workspace) => (
              <option
                key={workspace?._id || workspace?.uuid}
                value={workspace?._id}
              >
                {workspace?.name}
              </option>
            ))}
          </select>

          <div className="pending-application-actions">
            <button
              className="pending-application-reject-btn"
              type="button"
              disabled={isProcessing || checkedApplications.size === 0}
              onClick={() => handleUpdateRequests("rejected")}
            >
              {_t("reject") || "RECHAZAR"}
            </button>
            <button
              className="pending-application-accept-btn"
              type="button"
              style={
                !isProcessing &&
                selectedWorkspace &&
                checkedApplications.size > 0
                  ? { backgroundColor: "#a3eb64", color: "#253837" }
                  : {}
              }
              disabled={
                isProcessing ||
                !selectedWorkspace ||
                checkedApplications.size === 0
              }
              onClick={() => handleUpdateRequests("accepted")}
            >
              {_t("accept") || "ACEPTAR"}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PendingApplicationsModal;
